import { Link } from 'react-router-dom'
import type { Project } from '@/types/content'
import { ROUTES } from '@/lib/paths'
import { cn } from '@/lib/cn'
import { Icon, Surface } from '@/components/ui'
import { initialsOf } from './initials'
import styles from './ProjectCard.module.css'

interface ProjectCardProps {
  project: Project
  /** `big` is the 490px two-up card; `small` the 320px three-up card. */
  size?: 'big' | 'small'
}

/**
 * Links to a case study. Title and category sit above the image, with the
 * arrow button opposite them — the live site's order.
 */
export const ProjectCard = ({ project, size = 'big' }: ProjectCardProps) => (
  <Surface
    as={Link}
    to={ROUTES.project(project.slug)}
    pad={size === 'big' ? 25 : 20}
    radius="card"
    interactive
    className={cn(styles.card, size === 'small' && styles.small)}
  >
    <div className={styles.meta}>
      {/*
       * The project's own mark, when it has one. Decorative here: the title
       * is right beside it, so announcing the logo as well would read the
       * same name twice to anyone using a screen reader.
       */}
      {project.logo ? (
        <img
          src={project.logo}
          alt=""
          className={styles.logo}
          loading="lazy"
          decoding="async"
        />
      ) : null}

      <div className={styles.titleBlock}>
        <h3 className={cn('t-18', styles.title)}>{project.title}</h3>
        <p className={`t-12 ${styles.category}`}>{project.category}</p>
      </div>
      <span className={cn('orb', styles.arrow)} aria-hidden="true">
        <Icon name="arrowUpRight" size={18} />
      </span>
    </div>

    <div
      className={cn(
        styles.frame,
        size === 'big' ? styles.frameBig : styles.frameSmall,
      )}
    >
      {project.cover ? (
        <img
          src={project.cover}
          alt={`${project.title} — ${project.category} project`}
          className={styles.image}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className={styles.placeholder} role="img" aria-label={`${project.title} — cover image pending`}>
          <span className={styles.initials}>{initialsOf(project.title)}</span>
        </div>
      )}
    </div>
  </Surface>
)
